import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api, ApiFailure } from "../api/client";
import type {
  EventType,
  WebhookDelivery,
  WebhookDeliveryDetail,
  WebhookDeliveryStatus,
  WebhookEndpoint,
} from "../api/types";
import { EVENT_TYPES } from "../api/types";
import { useResource } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import { useSession } from "../store/session";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import {
  ActionError, Badge, Button, Card, Confirm, ErrorState, Field, Loading, Modal,
  PageHeader, inputClass,
} from "../components/ui";
import { formatDateTime, useInterval } from "./Calls";

// Endpoints de webhook y su historial de entregas.
//
// Las dos cosas van en la misma vista porque la pregunta que trae a alguien
// aquí es siempre la misma: "¿están llegando mis webhooks?". Con el historial
// en otra página habría que ir y volver para responderla.
//
// El polling solo corre mientras hay entregas en vuelo: una pendiente puede
// tardar horas en reintentarse (el backoff llega a 12 h), así que refrescar
// una tabla de entregas ya resueltas no cambiaría nada.
const POLL_INTERVAL_MS = 10000;

export function Webhooks() {
  const can = useSession((s) => s.can);

  const [statusFilter, setStatusFilter] = useState<WebhookDeliveryStatus | "">("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [borrando, setBorrando] = useState<WebhookEndpoint | null>(null);
  const [viendo, setViendo] = useState<WebhookDelivery | null>(null);

  // Las dos listas van por separado, y no es un detalle de organización: los
  // endpoints son unos pocos y se leen de una vez, mientras que las entregas
  // crecen una fila por evento y por reintento. Cargarlas juntas obligaba a
  // traerlas sin paginar, y lo que quedaba fuera no se podía ver.
  const endpointsRes = useResource<WebhookEndpoint[]>("/api/v1/webhook-endpoints");
  const endpoints = endpointsRes.data;

  const entregas = usePagedResource<WebhookDelivery>(
    `/api/v1/webhook-deliveries${statusFilter ? `?status=${statusFilter}` : ""}`,
  );
  const deliveries = entregas.data;
  const error = endpointsRes.error;

  function load(silent: boolean) {
    void endpointsRes.reload(silent);
    void entregas.reload(silent);
  }

  const hasInFlight = (deliveries ?? []).some(
    (d) => d.status === "pending" || d.status === "delivering",
  );
  useInterval(() => void entregas.reload(true), hasInFlight ? POLL_INTERVAL_MS : null);

  async function retry(delivery: WebhookDelivery) {
    setActionError(null);
    setBusyId(delivery.id);
    try {
      await api.post(`/api/v1/webhook-deliveries/${delivery.id}/retry`);
      await load(true);
    } catch (err) {
      setActionError(err instanceof ApiFailure ? err.error.message : "no se pudo reenviar");
    } finally {
      setBusyId(null);
    }
  }

  async function toggle(endpoint: WebhookEndpoint) {
    setActionError(null);
    setBusyId(endpoint.id);
    try {
      await api.patch(`/api/v1/webhook-endpoints/${endpoint.id}`, {
        status: endpoint.status === "active" ? "disabled" : "active",
      });
      await load(true);
    } catch (err) {
      setActionError(err instanceof ApiFailure ? err.error.message : "no se pudo cambiar");
    } finally {
      setBusyId(null);
    }
  }

  async function remove(endpoint: WebhookEndpoint) {
    setActionError(null);
    setBusyId(endpoint.id);
    try {
      await api.delete(`/api/v1/webhook-endpoints/${endpoint.id}`);
      await load(true);
    } catch (err) {
      setActionError(err instanceof ApiFailure ? err.error.message : "no se pudo eliminar");
    } finally {
      setBusyId(null);
    }
  }

  const columnasEndpoints = useMemo<ColumnDef<WebhookEndpoint, any>[]>(
    () => [
      {
        id: "url",
        accessorFn: (e) => `${e.url} ${e.description ?? ""}`,
        header: "URL",
        meta: { label: "URL" },
        cell: ({ row }) => (
          <>
            <div className="break-all font-mono text-xs text-slate-100">{row.original.url}</div>
            {row.original.description && (
              <div className="text-xs text-slate-500">{row.original.description}</div>
            )}
          </>
        ),
      },
      {
        id: "events",
        accessorFn: (e) => (e.events.length === 0 ? "todos" : e.events.join(", ")),
        header: "Tipos",
        enableSorting: false,
        meta: { label: "Tipos", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            {row.original.events.length === 0 ? (
              <span className="text-slate-500">todos</span>
            ) : (
              row.original.events.join(", ")
            )}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "8rem" },
        cell: ({ row }) => (
          <Badge tone={row.original.status === "active" ? "good" : "neutral"}>
            {row.original.status === "active" ? "Activo" : "Deshabilitado"}
          </Badge>
        ),
      },
      {
        id: "last_success_at",
        accessorKey: "last_success_at",
        header: "Último éxito",
        meta: { label: "Último éxito", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            {formatDateTime(row.original.last_success_at)}
          </span>
        ),
      },
      {
        id: "consecutive_failures",
        accessorKey: "consecutive_failures",
        header: "Fallos seguidos",
        meta: { label: "Fallos seguidos", align: "right", width: "9rem" },
        cell: ({ row }) => <FailureCount count={row.original.consecutive_failures} />,
      },
    ],
    [],
  );

  const accionesEndpoints = useMemo<DataTableRowAction<WebhookEndpoint>[]>(
    () =>
      can("webhooks.manage")
        ? [
            {
              label: "Activar o deshabilitar",
              disabled: (endpoint) => busyId === endpoint.id && "en curso…",
              onSelect: (endpoint) => void toggle(endpoint),
            },
            {
              label: "Eliminar",
              tone: "danger",
              onSelect: (endpoint) => setBorrando(endpoint),
            },
          ]
        : [],
    [can, busyId],
  );

  const columnasEntregas = useMemo<ColumnDef<WebhookDelivery, any>[]>(
    () => [
      {
        id: "event_type",
        accessorFn: (d) => `${d.event_type} ${d.event_id}`,
        header: "Evento",
        meta: { label: "Evento" },
        cell: ({ row }) => (
          <>
            <div className="text-slate-100">{row.original.event_type}</div>
            <div className="font-mono text-xs text-slate-500">{row.original.event_id}</div>
          </>
        ),
      },
      {
        id: "url",
        accessorKey: "url",
        header: "Destino",
        meta: { label: "Destino", hideOnMobile: true },
        cell: ({ row }) => (
          <div className="break-all font-mono text-xs text-slate-600">{row.original.url}</div>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado" },
        cell: ({ row }) => (
          <>
            <DeliveryBadge status={row.original.status} />
            {row.original.next_attempt_at && (
              <div className="mt-1 text-xs text-slate-500">
                reintento {formatDateTime(row.original.next_attempt_at)}
              </div>
            )}
          </>
        ),
      },
      {
        id: "attempts",
        accessorKey: "attempts",
        header: "Intentos",
        meta: { label: "Intentos", align: "right", width: "6rem" },
        cell: ({ row }) => <span className="text-slate-600">{row.original.attempts}</span>,
      },
      {
        id: "resultado",
        accessorFn: (d) => d.last_status_code ?? 0,
        header: "Resultado",
        meta: { label: "Resultado" },
        cell: ({ row }) => (
          <div className="text-xs">
            {row.original.last_status_code && (
              <div className="text-slate-300">HTTP {row.original.last_status_code}</div>
            )}
            {row.original.latency_ms !== null && (
              <div className="text-slate-500">{row.original.latency_ms} ms</div>
            )}
            {row.original.last_error && (
              <div className="mt-1 max-w-xs break-words text-rose-300">
                {row.original.last_error}
              </div>
            )}
          </div>
        ),
      },
      {
        id: "cuando",
        accessorFn: (d) => d.delivered_at ?? d.created_at,
        header: "Cuándo",
        meta: { label: "Cuándo", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            {formatDateTime(row.original.delivered_at ?? row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  const accionesEntregas = useMemo<DataTableRowAction<WebhookDelivery>[]>(() => {
    const acciones: DataTableRowAction<WebhookDelivery>[] = [
      { label: "Ver el JSON entregado", onSelect: (delivery) => setViendo(delivery) },
    ];
    if (can("webhooks.manage")) {
      acciones.push({
        label: "Reenviar",
        // Solo las entregadas y las agotadas se pueden reenviar: una en cola
        // ya va a salir sola.
        hidden: (delivery) =>
          delivery.status !== "dead" && delivery.status !== "delivered",
        disabled: (delivery) => busyId === delivery.id && "reenviando…",
        onSelect: (delivery) => void retry(delivery),
      });
    }
    return acciones;
  }, [can, busyId]);

  return (
    <div>
      <PageHeader
        title="Webhooks"
        description="Cada evento de llamada se entrega firmado a los endpoints suscritos. La entrega es al menos una vez: hay que deduplicar por el id del evento."
        action={
          can("webhooks.manage") && (
            <Button onClick={() => setCreating(true)}>Nuevo endpoint</Button>
          )
        }
      />

      {actionError && (
        <Card className="mb-4 border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
          {actionError}
        </Card>
      )}

      {creating && (
        <CreateEndpointForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load(false);
          }}
        />
      )}

      {endpoints === null && error === null && <Loading />}
      {endpoints === null && error !== null && (
        <ErrorState message={error} onRetry={() => load(false)} />
      )}

      {endpoints !== null && (
        <section className="mb-8">
          <h2 className="mb-2 text-sm font-medium text-slate-100">Endpoints</h2>
          <DataTable<WebhookEndpoint>
            data={endpoints}
            columns={columnasEndpoints}
            getRowId={(endpoint) => endpoint.id}
            loading={endpointsRes.loading}
            error={endpointsRes.error}
            onRetry={() => load(false)}
            caption="Endpoints de webhook"
            empty={{
              title: "Sin endpoints",
              description:
                "Registra uno para empezar a recibir eventos. Sin tipos seleccionados recibe todos.",
            }}
            search={{ placeholder: "Buscar por URL…" }}
            rowActions={accionesEndpoints}
            urlStateKey="endpoints"
          />
        </section>
      )}

      {deliveries !== null && (
        <section>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-medium text-slate-100">Entregas</h2>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              Estado
              <select
                className={inputClass}
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as WebhookDeliveryStatus | "")
                }
              >
                <option value="">Todas</option>
                <option value="pending">En cola</option>
                <option value="delivering">Entregando</option>
                <option value="delivered">Entregadas</option>
                <option value="dead">Agotadas</option>
              </select>
            </label>
          </div>

          {hasInFlight && (
            <p className="mb-2 text-xs text-slate-500">
              Hay entregas en vuelo; se actualiza cada {POLL_INTERVAL_MS / 1000} s.
            </p>
          )}

          <DataTable<WebhookDelivery>
            data={deliveries}
            columns={columnasEntregas}
            getRowId={(delivery) => delivery.id}
            loading={entregas.loading}
            error={entregas.error}
            onRetry={() => load(false)}
            caption="Entregas de webhook"
            empty={{ title: "Sin entregas" }}
            search={{ placeholder: "Buscar por evento o destino…" }}
            rowActions={accionesEntregas}
            onRowClick={(delivery) => setViendo(delivery)}
            hideableColumns
            urlStateKey="entregas"
            pagination={{
              pageSize: entregas.enEstaPagina,
              hasNext: entregas.hayMas,
              hasPrevious: entregas.hayAnterior,
              onNext: entregas.siguiente,
              onPrevious: entregas.anterior,
            }}
          />
        </section>
      )}

      {viendo !== null && (
        <CuerpoEntregado delivery={viendo} onClose={() => setViendo(null)} />
      )}

      {borrando !== null && (
        <Confirm
          title={`Eliminar «${borrando.url}»`}
          description="Los eventos dejarán de entregarse a esta URL. Las entregas ya hechas se conservan en el historial."
          confirmLabel="Eliminar el endpoint"
          busy={busyId === borrando.id}
          onConfirm={() => {
            const endpoint = borrando;
            setBorrando(null);
            void remove(endpoint);
          }}
          onCancel={() => setBorrando(null)}
        />
      )}
    </div>
  );
}

/**
 * FailureCount resalta un endpoint que lleva un rato rechazando todo.
 *
 * Un número que sube sin bajar suele ser un endpoint que el tenant retiró sin
 * desregistrarlo, y es algo que nadie nota hasta que echa en falta los
 * eventos.
 */
function FailureCount({ count }: { count: number }) {
  if (count === 0) return <span className="text-xs text-slate-500">—</span>;
  const tone = count >= 5 ? "bad" : "warn";
  return <Badge tone={tone}>{count}</Badge>;
}

/**
 * CuerpoEntregado enseña el JSON que recibió el endpoint.
 *
 * «Entregada» contesta si llegó, no **qué** llegó, y esa es la pregunta que
 * trae a alguien a esta pantalla cuando una integración no hace lo que
 * esperaba. Sin esto hay que ir a los logs del otro lado —o pedírnoslos—.
 *
 * El cuerpo se pide al abrir y no viene en la lista: son miles de entregas y
 * el payload de cada una no se mira casi nunca.
 */
function CuerpoEntregado({
  delivery,
  onClose,
}: {
  delivery: WebhookDelivery;
  onClose: () => void;
}) {
  const [detalle, setDetalle] = useState<WebhookDeliveryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    let vigente = true;
    api
      .get<WebhookDeliveryDetail>(`/api/v1/webhook-deliveries/${delivery.id}`)
      .then((d) => {
        if (vigente) setDetalle(d);
      })
      .catch((err) => {
        if (vigente) {
          setError(err instanceof ApiFailure ? err.error.message : "no se pudo cargar");
        }
      });
    return () => {
      vigente = false;
    };
  }, [delivery.id]);

  const json = detalle?.body ? JSON.stringify(detalle.body, null, 2) : "";

  return (
    <Modal
      title={delivery.event_type}
      description={
        <>
          Entregado a <span className="font-mono">{delivery.url}</span>
        </>
      }
      width="lg"
      onClose={onClose}
    >
      <div className="mb-4 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-4">
        <Dato etiqueta="Estado" valor={<DeliveryBadge status={delivery.status} />} />
        <Dato etiqueta="Intentos" valor={String(delivery.attempts)} />
        <Dato
          etiqueta="Respuesta"
          valor={delivery.last_status_code ? `HTTP ${delivery.last_status_code}` : "—"}
        />
        <Dato
          etiqueta="Latencia"
          valor={delivery.latency_ms === null ? "—" : `${delivery.latency_ms} ms`}
        />
      </div>

      {delivery.last_error && (
        <p className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
          {delivery.last_error}
        </p>
      )}

      {error !== null && <ActionError message={error} />}

      {detalle === null && error === null && (
        <p className="text-sm text-slate-500">Cargando el cuerpo…</p>
      )}

      {detalle !== null && detalle.body === null && (
        <p className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-slate-400">
          El contenido ya no está: {detalle.body_unavailable_reason ?? "el evento se purgó"}. La
          entrega se conserva; lo que se entregó, no.
        </p>
      )}

      {json !== "" && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Cuerpo
            </span>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(json);
                setCopiado(true);
              }}
            >
              {copiado ? "Copiado" : "Copiar"}
            </Button>
          </div>
          <pre className="max-h-96 overflow-auto rounded-lg border border-white/10 bg-[#0d1220] p-3 text-xs text-slate-300">
            {json}
          </pre>
          <p className="mt-3 text-xs text-slate-500">
            Es el cuerpo que recibió el endpoint, armado desde el evento guardado. Lo que no se
            puede reconstruir y por eso no sale: la firma y su marca de tiempo, que son de cada
            intento, y el enlace de una grabación, que se firma con caducidad.
          </p>
        </>
      )}
    </Modal>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div>
      <dt className="text-slate-500">{etiqueta}</dt>
      <dd className="mt-0.5 text-slate-200">{valor}</dd>
    </div>
  );
}

function DeliveryBadge({ status }: { status: WebhookDeliveryStatus }) {
  const label: Record<WebhookDeliveryStatus, string> = {
    pending: "En cola",
    delivering: "Entregando",
    delivered: "Entregada",
    dead: "Agotada",
  };
  const tone =
    status === "delivered" ? "good" : status === "dead" ? "bad" : "neutral";
  return <Badge tone={tone}>{label[status]}</Badge>;
}

function CreateEndpointForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<EventType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/webhook-endpoints", {
        url,
        description: description || undefined,
        events,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo registrar");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Nuevo endpoint"
      description="Recibirá un POST firmado por cada evento que elijas."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label="URL"
          hint="Debe ser https y resolver a una dirección pública. Se valida al registrar y en cada entrega."
          error={error ?? undefined}
        >
          <input
            className={inputClass}
            type="url"
            required
            placeholder="https://cliente.example.com/webhooks/iamedia"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Field>

        <Field label="Descripción" hint="Opcional, para distinguirlo en la lista.">
          <input
            className={inputClass}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <fieldset>
          <legend className="text-sm font-medium text-slate-300">Tipos de evento</legend>
          <p className="mt-1 text-xs text-slate-500">
            Sin ninguno marcado recibe <strong>todos</strong>.
          </p>
          <div className="mt-2 grid gap-1 sm:grid-cols-3">
            {EVENT_TYPES.map((type) => (
              <label key={type} className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={events.includes(type)}
                  onChange={(e) =>
                    setEvents((current) =>
                      e.target.checked
                        ? [...current, type]
                        : current.filter((t) => t !== type),
                    )
                  }
                />
                <code className="text-xs">{type}</code>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Registrando…" : "Registrar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
